/**
 * MANTENIMIENTO → ACTIVOS (FASE 8.1)
 *
 * Listado con filtros, alta, ficha (datos, estado, ubicación, historial y
 * QR), edición, cambio de estado, baja, etiqueta imprimible y catálogo de
 * tipos. Dominio propio: nada de esto toca Limpieza ni Calidad.
 *
 * La UI sólo decide qué BOTONES ofrecer (`puede()`); la barrera es la API
 * (permiso + ámbito por centro) y la RLS de 0058. Un activo fuera de ámbito
 * se ve igual que uno que no existe.
 *
 * FASE 8.2: la ficha (y por tanto el QR) es la puerta de entrada al trabajo
 * de campo: «Crear incidencia» y «Crear parte de trabajo» con cliente,
 * centro y activo ya puestos, los partes del activo y su documentación
 * (foto, manual, ficha técnica) en el mismo almacenamiento de evidencias.
 */
import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import {
  api,
  FalloApi,
  type ActivoDetalle,
  type ActivoResumen,
  type EstadoActivo,
  type EventoActivo,
  type EvidenciaHistorico,
  type ItemHistoricoActivo,
  type OpcionesActivos,
  type PlanPreventivoResumen,
  type TipoActivo,
  type TitularidadActivo,
  type UbicacionElegible,
} from '../api.ts';
import { BarraAcciones, Cargando, Error as ErrorCaja, Estado, fecha, fechaHora, FiltrosResponsive, Modal, Panel, useDatos, Vacio } from '../componentes/base.tsx';
import { estadoActivo } from '../estados.ts';
import {
  abrirArchivoRuta, EnlaceVolver, ErrorConEnlaces, EstadoImEtiqueta, EstadoPtEtiqueta, EvidenciasMnt, programacion, RESULTADOS, TIPOS_PT, useVolverAqui, type Volver,
} from '../componentes/mantenimiento.tsx';
import { EstadoPlanEtiqueta } from './preventivos.tsx';
import { CodigoQr } from '../componentes/qr.tsx';
import { urlFicha } from '../qr.ts';
import { Shell } from '../componentes/shell.tsx';
import { useToast } from '../componentes/toast.tsx';
import { useSesion } from '../contexto.tsx';
import { useListaUrl } from '../listaUrl.ts';

// ---------------------------------------------------------------- presentación

//  UX-1C: del mapa central (`estados.ts`).
export function etiquetaEstadoActivo(e: string): string {
  return estadoActivo(e).label;
}

export function EstadoActivoEtiqueta({ estado }: { estado: EstadoActivo | string }): ReactNode {
  return <Estado d={estadoActivo(estado)} />;
}

/** 0076: etiqueta de negocio de la titularidad. */
export const TITULARIDAD: Readonly<Record<TitularidadActivo, string>> = { CLIENTE: 'Cliente', EMPRESA: 'Empresa' };

export function TitularidadEtiqueta({ t }: { t: TitularidadActivo }): ReactNode {
  return <span className={`etiqueta ${t === 'EMPRESA' ? 'info' : 'neutra'}`}>{TITULARIDAD[t]}</span>;
}

/** El activo EMPRESA sólo lo modifica quien tiene `activos:gestionar_empresa` (ADMIN, CONSULTOR). */
function puedeModificar(a: { titularidad: TitularidadActivo }, puede: (p: string) => boolean, permiso: string): boolean {
  return puede(permiso) && (a.titularidad !== 'EMPRESA' || puede('activos:gestionar_empresa'));
}

const CAMPOS_EDITABLES: Readonly<Record<string, string>> = {
  centro: 'Centro',
  nombre: 'Nombre',
  descripcion: 'Descripción',
  idTipoActivo: 'Tipo',
  fabricante: 'Fabricante',
  modelo: 'Modelo',
  numeroSerie: 'Nº de serie',
  referenciaCliente: 'Referencia del cliente',
  fechaInstalacion: 'Fecha de instalación',
};

function Dato({ e, v }: { e: string; v: ReactNode }): ReactNode {
  return (
    <div className="dato">
      <div className="etiqueta-dato">{e}</div>
      <div className="valor-dato">{v === null || v === '' ? '—' : v}</div>
    </div>
  );
}


function erroresDe(e: unknown): Record<string, string> {
  return e instanceof FalloApi ? (e.erroresDeCampo ?? {}) : {};
}

// ---------------------------------------------------------------- listado

const DEFAULTS = { q: '', titularidad: '', cliente: '', centro: '', tipo: '', estado: '', pagina: '1' } as const;
const POR_PAGINA = 50;

export function PaginaActivos(): ReactNode {
  const { puede } = useSesion();
  const navegar = useNavigate();
  const lista = useListaUrl(DEFAULTS, 'pagina');
  const [altaAbierta, setAltaAbierta] = useState(false);

  const q = lista.valor('q');
  const titularidad = lista.valor('titularidad');
  const cliente = lista.valor('cliente');
  const centro = lista.valor('centro');
  const tipo = lista.valor('tipo');
  const estado = lista.valor('estado');
  const pagina = Number(lista.valor('pagina')) || 1;

  const opciones = useDatos(() => api.get<OpcionesActivos>('/api/activos/opciones'), []);
  const activos = useDatos(() => {
    const p = new URLSearchParams({ pagina: String(pagina), porPagina: String(POR_PAGINA) });
    if (q.trim() !== '') p.set('texto', q.trim());
    if (titularidad !== '') p.set('titularidad', titularidad);
    if (cliente !== '') p.set('idCliente', cliente);
    if (centro !== '') p.set('idCentro', centro);
    if (tipo !== '') p.set('idTipoActivo', tipo);
    if (estado !== '') p.set('estado', estado);
    return api.get<{ filas: ActivoResumen[]; total: number }>(`/api/activos?${p.toString()}`);
  }, [q, titularidad, cliente, centro, tipo, estado, pagina]);

  const clientes = useMemo(() => {
    const m = new Map<string, string>();
    for (const c of opciones.datos?.centros ?? []) m.set(c.cliente.id, c.cliente.nombre);
    return [...m.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [opciones.datos]);
  const centrosFiltrados = (opciones.datos?.centros ?? []).filter((c) => cliente === '' || c.cliente.id === cliente);
  const contador = [titularidad, cliente, centro, tipo, estado].filter((v) => v !== '').length;

  return (
    <Shell
      migas={[{ texto: 'Mantenimiento' }, { texto: 'Activos' }]}
      titulo="Activos"
      acciones={puede('activos:crear') ? <button className="principal" onClick={() => setAltaAbierta(true)}>Nuevo activo</button> : undefined}
    >
      <div className="panel">
        <div className="panel-cuerpo">
          <FiltrosResponsive
            contador={contador}
            busqueda={
              <input
                type="search"
                aria-label="Buscar por código, nombre, nº de serie o referencia"
                placeholder="Buscar código, nombre, nº serie…"
                value={q}
                onChange={(ev) => lista.fijar('q', ev.target.value)}
              />
            }
          >
            <div className="filtros-rejilla">
              <label className="campo">
                <span>Titularidad</span>
                <select value={titularidad} onChange={(ev) => lista.fijar('titularidad', ev.target.value)}>
                  <option value="">Todas</option>
                  <option value="CLIENTE">Cliente</option>
                  <option value="EMPRESA">Empresa</option>
                </select>
              </label>
              <label className="campo">
                <span>Cliente</span>
                <select value={cliente} onChange={(ev) => lista.fijarVarios({ cliente: ev.target.value, centro: '' })}>
                  <option value="">Todos</option>
                  {clientes.map(([id, nombre]) => <option key={id} value={id}>{nombre}</option>)}
                </select>
              </label>
              <label className="campo">
                <span>Centro</span>
                <select value={centro} onChange={(ev) => lista.fijar('centro', ev.target.value)}>
                  <option value="">Todos</option>
                  {centrosFiltrados.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
                </select>
              </label>
              <label className="campo">
                <span>Tipo</span>
                <select value={tipo} onChange={(ev) => lista.fijar('tipo', ev.target.value)}>
                  <option value="">Todos</option>
                  {(opciones.datos?.tipos ?? []).map((t) => <option key={t.id} value={t.id}>{t.nombre}</option>)}
                </select>
              </label>
              <label className="campo">
                <span>Estado</span>
                <select value={estado} onChange={(ev) => lista.fijar('estado', ev.target.value)}>
                  <option value="">En uso (sin bajas)</option>
                  <option value="OPERATIVO">Operativo</option>
                  <option value="FUERA_SERVICIO">Fuera de servicio</option>
                  <option value="BAJA">Baja</option>
                  <option value="TODOS">Todos</option>
                </select>
              </label>
              {contador > 0 && <button type="button" onClick={() => lista.limpiar()}>Limpiar filtros</button>}
            </div>
          </FiltrosResponsive>
        </div>
      </div>

      <div className="panel">
        <Panel
          estado={activos}
          vacio={<Vacio titulo="Sin activos" />}
        >
          {(d) =>
            d.filas.length === 0 ? (
              <Vacio titulo={lista.esPorDefecto ? 'Todavía no hay activos' : 'Ningún activo coincide con los filtros'}>
                {lista.esPorDefecto && puede('activos:crear') && <p>Da de alta el primero con «Nuevo activo».</p>}
              </Vacio>
            ) : (
              <>
                <div className="tabla-ots-desktop tabla-desliza">
                  <table>
                    <thead>
                      <tr>
                        <th>Código</th><th>Nombre</th><th>Titularidad</th><th>Tipo</th><th>Cliente</th><th>Centro</th><th>Ubicación</th><th>Estado</th>
                      </tr>
                    </thead>
                    <tbody>
                      {d.filas.map((a) => (
                        <tr key={a.id}>
                          <td className="mono"><Link to={`/activos/${a.id}`}>{a.codigo}</Link></td>
                          <td>{a.nombre}</td>
                          <td><TitularidadEtiqueta t={a.titularidad} /></td>
                          <td>{a.tipo.nombre}</td>
                          <td>{a.cliente?.nombre ?? '—'}</td>
                          <td>{a.centro?.nombre ?? 'Sin asignar'}</td>
                          <td>{a.ubicacion?.nombre ?? '—'}</td>
                          <td><EstadoActivoEtiqueta estado={a.estado} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <ul className="lista-ots-movil" aria-label="Activos">
                  {d.filas.map((a) => (
                    <li key={a.id} className="tarjeta-ot">
                      <div className="tarjeta-ot-cabecera">
                        <Link className="tarjeta-ot-codigo mono" to={`/activos/${a.id}`}>{a.codigo}</Link>
                        <EstadoActivoEtiqueta estado={a.estado} />
                      </div>
                      <div className="tarjeta-ot-datos">
                        <span className="tarjeta-ot-fecha">{a.nombre} <TitularidadEtiqueta t={a.titularidad} /></span>
                        <span className="tarjeta-ot-secundario">{a.tipo.nombre} · {a.centro?.nombre ?? 'Sin centro asignado'}</span>
                        {a.ubicacion !== null && <span className="tarjeta-ot-secundario">{a.ubicacion.nombre}</span>}
                      </div>
                    </li>
                  ))}
                </ul>
                {d.total > POR_PAGINA && (
                  <div className="panel-cuerpo fila separa">
                    <button disabled={pagina <= 1} onClick={() => lista.fijar('pagina', String(pagina - 1))}>Anterior</button>
                    <span className="suave">Página {pagina} de {Math.ceil(d.total / POR_PAGINA)} · {d.total} activos</span>
                    <button disabled={pagina * POR_PAGINA >= d.total} onClick={() => lista.fijar('pagina', String(pagina + 1))}>Siguiente</button>
                  </div>
                )}
              </>
            )
          }
        </Panel>
      </div>

      {altaAbierta && opciones.datos !== null && (
        <FormAltaActivo
          opciones={opciones.datos}
          permiteEmpresa={puede('activos:gestionar_empresa')}
          cerrar={() => setAltaAbierta(false)}
          alCrear={(a) => navegar(`/activos/${a.id}`)}
        />
      )}
    </Shell>
  );
}

// ---------------------------------------------------------------- ubicaciones de un centro

function useUbicaciones(idCentro: string): UbicacionElegible[] {
  const [filas, setFilas] = useState<UbicacionElegible[]>([]);
  useEffect(() => {
    let vivo = true;
    setFilas([]);
    if (idCentro === '') return;
    api
      .get<{ filas: UbicacionElegible[] }>(`/api/activos/ubicaciones?idCentro=${idCentro}`)
      .then((r) => { if (vivo) setFilas(r.filas); })
      .catch(() => { if (vivo) setFilas([]); });
    return () => { vivo = false; };
  }, [idCentro]);
  return filas;
}

// ---------------------------------------------------------------- alta

function FormAltaActivo({
  opciones,
  permiteEmpresa,
  cerrar,
  alCrear,
}: {
  opciones: OpcionesActivos;
  /** 0076: sólo ADMIN / CONSULTOR (`activos:gestionar_empresa`) eligen titularidad EMPRESA. */
  permiteEmpresa: boolean;
  cerrar: () => void;
  alCrear: (a: ActivoDetalle) => void;
}): ReactNode {
  const [titularidad, setTitularidad] = useState<TitularidadActivo>('CLIENTE');
  const [idCentro, setIdCentro] = useState(opciones.centros.length === 1 ? opciones.centros[0]!.id : '');
  const [idTipoActivo, setIdTipoActivo] = useState('');
  const [nombre, setNombre] = useState('');
  const [idUbicacion, setIdUbicacion] = useState('');
  const [fabricante, setFabricante] = useState('');
  const [modelo, setModelo] = useState('');
  const [numeroSerie, setNumeroSerie] = useState('');
  const [fechaInstalacion, setFechaInstalacion] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const ubicaciones = useUbicaciones(idCentro);
  const centro = opciones.centros.find((c) => c.id === idCentro);
  const campos = erroresDe(error);

  async function enviar(ev: FormEvent): Promise<void> {
    ev.preventDefault();
    setEnviando(true);
    setError(null);
    try {
      const a = await api.post<ActivoDetalle>('/api/activos', {
        titularidad,
        //  EMPRESA: el centro es opcional (sin centro, tampoco ubicación).
        ...(titularidad === 'EMPRESA' && idCentro === '' ? {} : { idCentro }),
        idTipoActivo, nombre,
        idUbicacion: idUbicacion === '' ? null : idUbicacion,
        fabricante, modelo, numeroSerie,
        fechaInstalacion: fechaInstalacion === '' ? null : fechaInstalacion,
      });
      alCrear(a);
    } catch (e) {
      setError(e);
    } finally {
      setEnviando(false);
    }
  }

  return (
    <Modal titulo="Nuevo activo" cerrar={cerrar}>
      <form onSubmit={(ev) => void enviar(ev)} aria-label="Nuevo activo">
        {permiteEmpresa && (
          <fieldset className="campo">
            <legend>Titularidad</legend>
            <div className="fila" style={{ gap: 16, flexWrap: 'wrap' }}>
              <label className="fila" style={{ gap: 6 }}>
                <input type="radio" name="alta-titularidad" checked={titularidad === 'CLIENTE'} onChange={() => setTitularidad('CLIENTE')} />
                Cliente
              </label>
              <label className="fila" style={{ gap: 6 }}>
                <input type="radio" name="alta-titularidad" checked={titularidad === 'EMPRESA'} onChange={() => setTitularidad('EMPRESA')} />
                Empresa
              </label>
            </div>
            {campos['titularidad'] !== undefined && <div className="error-campo">{campos['titularidad']}</div>}
          </fieldset>
        )}
        <div className="campo">
          <label htmlFor="alta-centro">{titularidad === 'EMPRESA' ? 'Centro (opcional)' : 'Centro'}</label>
          <select id="alta-centro" value={idCentro} required={titularidad === 'CLIENTE'} onChange={(ev) => { setIdCentro(ev.target.value); setIdUbicacion(''); }}>
            <option value="">{titularidad === 'EMPRESA' ? 'Sin centro asignado' : 'Elige un centro…'}</option>
            {opciones.centros.map((c) => <option key={c.id} value={c.id}>{c.cliente.nombre} — {c.nombre}</option>)}
          </select>
          {titularidad === 'CLIENTE' && centro !== undefined && <div className="suave pequena">Cliente: {centro.cliente.nombre}</div>}
          {titularidad === 'EMPRESA' && <div className="suave pequena">Equipo de la empresa prestadora: no tiene cliente.</div>}
          {campos['idCentro'] !== undefined && <div className="error-campo">{campos['idCentro']}</div>}
        </div>
        <div className="campo">
          <label htmlFor="alta-tipo">Tipo</label>
          <select id="alta-tipo" value={idTipoActivo} required onChange={(ev) => setIdTipoActivo(ev.target.value)}>
            <option value="">Elige un tipo…</option>
            {opciones.tipos.map((t) => <option key={t.id} value={t.id}>{t.nombre}</option>)}
          </select>
          {campos['idTipoActivo'] !== undefined && <div className="error-campo">{campos['idTipoActivo']}</div>}
        </div>
        <CampoLibre id="alta-nombre" label="Nombre" valor={nombre} cambiar={setNombre} error={campos['nombre']} requerido />
        <div className="campo">
          <label htmlFor="alta-ubicacion">Ubicación (opcional)</label>
          <select id="alta-ubicacion" value={idUbicacion} disabled={idCentro === ''} onChange={(ev) => setIdUbicacion(ev.target.value)}>
            <option value="">Sin ubicación</option>
            {ubicaciones.map((u) => <option key={u.id} value={u.id}>{u.ruta}</option>)}
          </select>
          {campos['idUbicacion'] !== undefined && <div className="error-campo">{campos['idUbicacion']}</div>}
        </div>
        <CampoLibre id="alta-fabricante" label="Fabricante" valor={fabricante} cambiar={setFabricante} error={campos['fabricante']} />
        <CampoLibre id="alta-modelo" label="Modelo" valor={modelo} cambiar={setModelo} error={campos['modelo']} />
        <CampoLibre id="alta-serie" label="Nº de serie" valor={numeroSerie} cambiar={setNumeroSerie} error={campos['numeroSerie']} />
        <CampoLibre id="alta-fecha" label="Fecha de instalación" tipo="date" valor={fechaInstalacion} cambiar={setFechaInstalacion} error={campos['fechaInstalacion']} />
        {error !== null && <ErrorCaja error={error} />}
        <div className="fila">
          <button type="submit" className="principal" disabled={enviando}>{enviando ? 'Creando…' : 'Crear activo'}</button>
          <button type="button" onClick={cerrar}>Cancelar</button>
        </div>
      </form>
    </Modal>
  );
}

function CampoLibre({
  id, label, valor, cambiar, error, requerido, tipo = 'text', multilinea,
}: {
  id: string; label: string; valor: string; cambiar: (v: string) => void; error?: string | undefined;
  requerido?: boolean; tipo?: 'text' | 'date'; multilinea?: boolean;
}): ReactNode {
  return (
    <div className="campo">
      <label htmlFor={id}>{label}</label>
      {multilinea === true ? (
        <textarea id={id} value={valor} rows={3} onChange={(ev) => cambiar(ev.target.value)} aria-invalid={error !== undefined ? 'true' : undefined} />
      ) : (
        <input id={id} type={tipo} value={valor} required={requerido} onChange={(ev) => cambiar(ev.target.value)} aria-invalid={error !== undefined ? 'true' : undefined} />
      )}
      {error !== undefined && <div className="error-campo">{error}</div>}
    </div>
  );
}

// ---------------------------------------------------------------- ficha

type Dialogo = 'editar' | 'fuera' | 'operativo' | 'baja' | null;

export function PaginaActivo(): ReactNode {
  const { id = '' } = useParams();
  const { puede } = useSesion();
  const activo = useDatos(() => api.get<ActivoDetalle>(`/api/activos/${encodeURIComponent(id)}`), [id]);
  const [version, setVersion] = useState(0);
  const [dialogo, setDialogo] = useState<Dialogo>(null);
  const toast = useToast();
  const volverAqui = useVolverAqui(`el activo ${activo.datos?.nombre ?? ''}`.trim());

  /** UX-1C: `exito` → toast ligero; los errores siguen dentro de cada diálogo. */
  function recargar(exito?: string): void {
    setDialogo(null);
    activo.recargar();
    setVersion((v) => v + 1);
    if (exito !== undefined) toast.exito(exito);
  }

  const noEncontrado = activo.error instanceof FalloApi && (activo.error.estado === 404 || activo.error.estado === 403);
  if (noEncontrado) {
    return (
      <Shell migas={[{ texto: 'Mantenimiento' }, { texto: 'Activos', a: '/activos' }]} titulo="Activo no disponible">
        <div className="panel">
          <Vacio titulo="Este activo no existe o no tienes acceso">
            <p>Si has escaneado un QR, comprueba que el activo es de uno de tus centros.</p>
            <Link to="/activos">Volver a Activos</Link>
          </Vacio>
        </div>
      </Shell>
    );
  }

  const a = activo.datos;
  const activoVivo = a !== null && a.estado !== 'BAJA';
  return (
    <Shell
      migas={[{ texto: 'Mantenimiento' }, { texto: 'Activos', a: '/activos' }, { texto: a?.codigo ?? '…' }]}
      titulo={a === null ? 'Activo' : a.nombre}
      estado={a === null ? undefined : <EstadoActivoEtiqueta estado={a.estado} />}
      meta={a === null ? undefined : (
        <><span className="mono">{a.codigo}</span><TitularidadEtiqueta t={a.titularidad} /><span>{a.centro?.nombre ?? 'Sin centro asignado'}{a.ubicacion !== null ? ` · ${a.ubicacion.nombre}` : ''}</span></>
      )}
      acciones={a === null || !activoVivo ? undefined : (() => {
        //  UX-1B: principal = lo que más se hace sobre un activo (abrir trabajo); Editar, secundaria; el resto,
        //  incluida la baja (destructiva), en «Más acciones». Los permisos, como siempre, deciden qué existe.
        //  F12.2: también un activo EMPRESA, en el centro donde está ahora; sin centro no hay dónde trabajar.
        const centro = a.centro;
        const parte = centro !== null && puede('partes:gestionar') ? `/mantenimiento/partes/nuevo?idCentro=${centro.id}&idActivo=${a.id}` : null;
        const incidencia = centro !== null && puede('incidencias_mnt:crear') ? `/mantenimiento/incidencias/nueva?idCentro=${centro.id}&idActivo=${a.id}` : null;
        const gestiona = puedeModificar(a, puede, 'activos:gestionar');
        return (
          <BarraAcciones etiqueta="Acciones del activo"
            principal={parte !== null ? <Link className="boton principal" to={parte} state={volverAqui}>Crear parte de trabajo</Link>
              : incidencia !== null ? <Link className="boton principal" to={incidencia} state={volverAqui}>Crear incidencia</Link> : undefined}
            secundaria={gestiona ? <button onClick={() => setDialogo('editar')}>Editar</button> : undefined}
            mas={[
              parte !== null && incidencia !== null && { texto: 'Crear incidencia', a: incidencia },
              gestiona && a.estado === 'OPERATIVO' && { texto: 'Poner fuera de servicio', alPulsar: () => setDialogo('fuera') },
              gestiona && a.estado === 'FUERA_SERVICIO' && { texto: 'Volver a operativo', alPulsar: () => setDialogo('operativo') },
              puedeModificar(a, puede, 'activos:baja') && { texto: 'Dar de baja', alPulsar: () => setDialogo('baja'), peligro: true },
            ]} />
        );
      })()}
    >
      <EnlaceVolver />
      <Panel estado={activo}>
        {(d) => (
          <>
            {d.titularidad === 'EMPRESA' && d.centro === null && (
              <div className="nota aviso" role="status">
                Este equipo de la empresa no está asignado a ningún centro. Asigna primero el activo a un centro para crear un parte de mantenimiento.
              </div>
            )}
            {d.estado !== 'OPERATIVO' && (
              <div className={`nota ${d.estado === 'BAJA' ? 'error' : 'aviso'}`} role="status">
                <strong>{etiquetaEstadoActivo(d.estado)}.</strong> {d.motivoEstado}
                {d.estado === 'BAJA' && d.bajaPor !== null && <> · {d.bajaPor.nombre}, {fechaHora(d.bajaEn)}. Ya no admite cambios.</>}
              </div>
            )}
            <div className="panel">
              <div className="panel-cabecera"><h2>Información</h2></div>
              <div className="panel-cuerpo datos">
                <Dato e="Código" v={<span className="mono">{d.codigo}</span>} />
                <Dato e="Estado" v={<EstadoActivoEtiqueta estado={d.estado} />} />
                <Dato e="Titularidad" v={d.titularidad === 'EMPRESA' ? 'Empresa prestadora' : 'Cliente'} />
                <Dato e="Tipo" v={d.tipo.nombre} />
                {d.cliente !== null && <Dato e="Cliente" v={d.cliente.nombre} />}
                <Dato e="Centro" v={d.centro?.nombre ?? 'Sin asignar'} />
                <Dato e="Ubicación" v={d.ubicacion?.nombre ?? 'Sin ubicación'} />
                <Dato e="Fabricante" v={d.fabricante} />
                <Dato e="Modelo" v={d.modelo} />
                <Dato e="Nº de serie" v={d.numeroSerie === null ? null : <span className="mono">{d.numeroSerie}</span>} />
                <Dato e="Referencia del cliente" v={d.referenciaCliente} />
                <Dato e="Fecha de instalación" v={d.fechaInstalacion === null ? null : fecha(d.fechaInstalacion)} />
                <Dato e="Alta" v={`${d.creadoPor.nombre} · ${fechaHora(d.creadoEn)}`} />
              </div>
              {d.descripcion !== null && <div className="panel-cuerpo"><p style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{d.descripcion}</p></div>}
            </div>

            <div className="panel">
              <div className="panel-cabecera fila separa">
                <h2>QR</h2>
                <Link to={`/activos/${d.id}/etiqueta`}>Etiqueta imprimible</Link>
              </div>
              <div className="panel-cuerpo fila" style={{ flexWrap: 'wrap', alignItems: 'flex-start' }}>
                <CodigoQr texto={urlFicha(d.id)} tamano={148} titulo={`QR del activo ${d.codigo}`} />
                <p className="suave pequena" style={{ maxWidth: 360 }}>
                  Abre esta ficha. Exige iniciar sesión y tener acceso al centro: el QR no contiene
                  ninguna clave ni dato privado.
                </p>
              </div>
            </div>
          </>
        )}
      </Panel>

      {/* F12.2: «¿qué le ha pasado y qué le han hecho?» en una sola lista (sustituye a las listas sueltas de partes,
          incidencias e historial). Los planes preventivos siguen aparte: son lo que VA a pasar. */}
      {a !== null && <HistoricoActivo idActivo={a.id} centroActual={a.centro?.id ?? null} version={version} volver={volverAqui} />}
      {puede('preventivos:leer') && a !== null && <PreventivosDelActivo idActivo={a.id} />}

      {a !== null && a.titularidad !== 'EMPRESA' && (
        <EvidenciasMnt
          titulo="Documentación"
          rutaLista={`/api/activos/${a.id}/evidencias`}
          rutaSubida={`/api/activos/${a.id}/evidencias`}
          puedeSubir={activoVivo && puede('activos:gestionar')}
          puedeAnular={puede('activos:gestionar')}
          puedePublicar={puede('activos:gestionar')}
          contextos={['DOCUMENTO', 'GENERAL']}
        />
      )}


      {a !== null && dialogo === 'editar' && <FormEditarActivo activo={a} cerrar={() => setDialogo(null)} alGuardar={() => recargar('Cambios guardados.')} />}
      {a !== null && (dialogo === 'fuera' || dialogo === 'operativo') && (
        <DialogoMotivo
          titulo={dialogo === 'fuera' ? 'Poner fuera de servicio' : 'Volver a operativo'}
          explicacion={dialogo === 'fuera' ? 'Indica qué le pasa al equipo. Quedará en el historial.' : 'Puedes añadir una nota (opcional).'}
          obligatorio={dialogo === 'fuera'}
          textoBoton={dialogo === 'fuera' ? 'Poner fuera de servicio' : 'Volver a operativo'}
          enviar={(motivo) => api.post(`/api/activos/${a.id}/estado`, { estado: dialogo === 'fuera' ? 'FUERA_SERVICIO' : 'OPERATIVO', motivo })}
          cerrar={() => setDialogo(null)}
          alTerminar={() => recargar(dialogo === 'fuera' ? 'Activo puesto fuera de servicio.' : 'Activo de nuevo operativo.')}
        />
      )}
      {a !== null && dialogo === 'baja' && (
        <DialogoMotivo
          titulo={`Dar de baja ${a.codigo}`}
          explicacion="La baja es definitiva: el activo conserva su historial, pero ya no se podrá editar ni cambiar de estado, y dejará de aparecer en el listado por defecto."
          obligatorio
          textoBoton="Dar de baja"
          peligro
          enviar={(motivo) => api.post(`/api/activos/${a.id}/baja`, { motivo })}
          cerrar={() => setDialogo(null)}
          alTerminar={() => recargar(`Activo ${a.codigo} dado de baja.`)}
        />
      )}
    </Shell>
  );
}

/** UX-1D — planes preventivos de este activo (vigentes primero), con su próxima fecha. */
function PreventivosDelActivo({ idActivo }: { idActivo: string }): ReactNode {
  const planes = useDatos(() => api.get<{ filas: PlanPreventivoResumen[] }>(`/api/mantenimiento/preventivos?idActivo=${idActivo}&estado=TODOS&porPagina=50`), [idActivo]);
  return (
    <section className="panel" aria-label="Planes preventivos del activo">
      <div className="panel-cabecera"><h2>Planes preventivos</h2></div>
      <div className="panel-cuerpo">
        <Panel estado={planes}>
          {(d) => (d.filas.length === 0 ? <p className="suave">Este activo no tiene planes preventivos. Se crean desde <Link to="/mantenimiento/preventivos">Preventivos</Link>.</p> : (
            <ul className="lista-relacionados">{d.filas.map((pp) => (
              <li key={pp.id}>
                <Link to={`/mantenimiento/preventivos/${pp.id}`} className="mono">{pp.codigo}</Link>
                <span>{pp.nombre}</span>
                <span className="suave pequena">{pp.frecuencia} · {pp.horaProgramada}{pp.proximaFecha !== null && ` · próxima ${programacion(pp.proximaFecha, null)}`}</span>
                <EstadoPlanEtiqueta estado={pp.estado} />
              </li>
            ))}</ul>
          ))}
        </Panel>
      </div>
    </section>
  );
}

/**
 * F12.2 — histórico consolidado del activo: incidencias, partes (con sus visitas: diagnóstico, trabajo, resultado
 * y evidencias) y cambios del propio equipo, del más reciente al más antiguo. Cada parte dice en qué centro se
 * hizo si no es el actual (un equipo de la empresa puede haberse movido).
 */
type FiltroHistorico = 'TODO' | 'PARTE' | 'INCIDENCIA' | 'ACTIVO';
const FILTROS_HISTORICO: readonly [FiltroHistorico, string][] = [['TODO', 'Todo'], ['PARTE', 'Trabajos'], ['INCIDENCIA', 'Incidencias'], ['ACTIVO', 'Cambios del equipo']];

function HistoricoActivo({ idActivo, centroActual, version, volver }: {
  idActivo: string; centroActual: string | null; version: number; volver: { volver: Volver };
}): ReactNode {
  const datos = useDatos(() => api.get<{ items: ItemHistoricoActivo[] }>(`/api/activos/${idActivo}/historico`), [idActivo, version]);
  const [filtro, setFiltro] = useState<FiltroHistorico>('TODO');
  return (
    <section className="panel" aria-label="Historial del activo">
      <div className="panel-cabecera"><h2>Historial</h2></div>
      <div className="panel-cuerpo">
        <div className="fila filtros-historico" role="group" aria-label="Qué mostrar" style={{ flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
          {FILTROS_HISTORICO.map(([k, t]) => (
            <button key={k} type="button" className={filtro === k ? 'principal' : 'secundario'} aria-pressed={filtro === k} onClick={() => setFiltro(k)}>{t}</button>
          ))}
        </div>
        <Panel estado={datos}>
          {(d) => {
            const items = d.items.filter((x) => filtro === 'TODO' || x.tipo === filtro);
            return items.length === 0 ? <p className="suave">Nada que mostrar todavía.</p> : (
              <ol className="historico-activo">
                {items.map((x) => <ItemHistorico key={`${x.tipo}-${x.tipo === 'ACTIVO' ? x.evento.id : x.id}`} item={x} centroActual={centroActual} volver={volver} />)}
              </ol>
            );
          }}
        </Panel>
      </div>
    </section>
  );
}

function EvidenciasHistorico({ evidencias }: { evidencias: EvidenciaHistorico[] }): ReactNode {
  if (evidencias.length === 0) return null;
  return (
    <ul className="lista-sin-estilo evidencias-historico">
      {evidencias.map((e) => (
        <li key={e.id}>
          <button type="button" className="enlace" onClick={() => void abrirArchivoRuta(`/api/mantenimiento/evidencias/${e.id}/archivo`)}>{e.nombre}</button>{' '}
          <span className={`etiqueta ${e.visibilidad === 'CLIENTE' ? 'info' : 'neutra'}`}>{e.visibilidad === 'CLIENTE' ? 'Visible para el cliente' : 'Interna'}</span>
        </li>
      ))}
    </ul>
  );
}

function ItemHistorico({ item: x, centroActual, volver }: { item: ItemHistoricoActivo; centroActual: string | null; volver: { volver: Volver } }): ReactNode {
  if (x.tipo === 'ACTIVO') {
    const e = x.evento;
    return (
      <li className="item-historico">
        <div className="suave pequena">{fechaHora(e.ocurridoEn)} · Cambio del equipo</div>
        <div><strong>{tituloEvento(e)}</strong></div>
        {e.tipo === 'EDICION' && e.cambios !== null && (
          <ul className="pequena" style={{ margin: '2px 0', paddingLeft: 16 }}>
            {Object.entries(e.cambios).map(([k, c]) => (
              <li key={k}>{CAMPOS_EDITABLES[k] ?? k}{k !== 'idTipoActivo' && <>: {valorLegible(c.antes)} → {valorLegible(c.despues)}</>}</li>
            ))}
          </ul>
        )}
        {e.motivo !== null && <div className="pequena">Motivo: {e.motivo}</div>}
        <div className="suave pequena">{e.usuario.nombre}</div>
      </li>
    );
  }
  if (x.tipo === 'INCIDENCIA') {
    return (
      <li className="item-historico">
        <div className="suave pequena">{fechaHora(x.fecha)} · Incidencia{x.centro.id !== centroActual ? ` · en ${x.centro.nombre}` : ''}</div>
        <div className="fila" style={{ flexWrap: 'wrap', gap: 6 }}>
          <Link to={`/mantenimiento/incidencias/${x.id}`} state={volver}><strong>{x.titulo}</strong></Link>
          <EstadoImEtiqueta estado={x.estado} />
          {x.origen === 'CLIENTE' && <span className="etiqueta info">Comunicada por el cliente</span>}
        </div>
        {x.resolucion !== null && <div className="pequena">Resolución: {x.resolucion}</div>}
      </li>
    );
  }
  return (
    <li className="item-historico">
      <div className="suave pequena">{fechaHora(x.fecha)} · {TIPOS_PT[x.tipoPt] ?? x.tipoPt}{x.centro.id !== centroActual ? ` · en ${x.centro.nombre}` : ''}</div>
      <div className="fila" style={{ flexWrap: 'wrap', gap: 6 }}>
        <Link to={`/mantenimiento/partes/${x.id}`} state={volver}><strong>{x.titulo}</strong></Link>
        <EstadoPtEtiqueta estado={x.estado} />
        <span className="suave pequena">{x.codigo}</span>
      </div>
      {x.preventivo !== null && <div className="pequena">Mantenimiento preventivo: <Link to={`/mantenimiento/preventivos/${x.preventivo.id}`}>{x.preventivo.nombre}</Link></div>}
      {x.intervenciones.length > 0 && (
        <ol className="visitas-historico">
          {x.intervenciones.map((iv, k) => (
            <li key={iv.id}>
              <div className="pequena"><strong>Visita {k + 1}</strong> · {iv.tecnico} · {fechaHora(iv.inicioEn)}{iv.finEn === null ? ' · en curso' : ''}
                {iv.resultado !== null && <> <span className={`etiqueta ${iv.resultado === 'RESUELTO' ? 'exito' : 'aviso'}`}>{RESULTADOS[iv.resultado] ?? iv.resultado}</span></>}</div>
              {iv.diagnostico !== null && <div className="pequena">Diagnóstico: {iv.diagnostico}</div>}
              {iv.trabajoRealizado !== null && <div className="pequena">Trabajo: {iv.trabajoRealizado}</div>}
              <EvidenciasHistorico evidencias={iv.evidencias} />
            </li>
          ))}
        </ol>
      )}
      <EvidenciasHistorico evidencias={x.evidencias} />
      {x.resumenPublico !== null && <div className="pequena">Resumen para el cliente: {x.resumenPublico}</div>}
    </li>
  );
}

function valorLegible(v: unknown): string {
  return v === null || v === undefined || v === '' ? '(vacío)' : String(v);
}

function tituloEvento(e: EventoActivo): string {
  switch (e.tipo) {
    case 'ALTA': return 'Alta del activo';
    case 'BAJA': return 'Baja del activo';
    case 'ESTADO': return `Estado: ${etiquetaEstadoActivo(e.estadoAnterior ?? '')} → ${etiquetaEstadoActivo(e.estadoNuevo ?? '')}`;
    case 'UBICACION': return `Ubicación: ${e.ubicacionAnterior?.nombre ?? 'sin ubicación'} → ${e.ubicacionNueva?.nombre ?? 'sin ubicación'}`;
    case 'EDICION': return 'Datos editados';
    default: return e.tipo;
  }
}

// ---------------------------------------------------------------- edición

function FormEditarActivo({ activo, cerrar, alGuardar }: { activo: ActivoDetalle; cerrar: () => void; alGuardar: () => void }): ReactNode {
  const tipos = useDatos(() => api.get<{ filas: TipoActivo[] }>('/api/tipos-activo'), []);
  const empresa = activo.titularidad === 'EMPRESA';
  //  0076: el centro sólo se reasigna en un activo EMPRESA (opciones: centros del ámbito).
  const opciones = useDatos(() => (empresa ? api.get<OpcionesActivos>('/api/activos/opciones') : Promise.resolve(null)), [empresa]);
  const [idCentro, setIdCentro] = useState(activo.centro?.id ?? '');
  const ubicaciones = useUbicaciones(idCentro);
  const [v, setV] = useState({
    nombre: activo.nombre,
    descripcion: activo.descripcion ?? '',
    idTipoActivo: activo.tipo.id,
    idUbicacion: activo.ubicacion?.id ?? '',
    fabricante: activo.fabricante ?? '',
    modelo: activo.modelo ?? '',
    numeroSerie: activo.numeroSerie ?? '',
    referenciaCliente: activo.referenciaCliente ?? '',
    fechaInstalacion: activo.fechaInstalacion ?? '',
  });
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const campos = erroresDe(error);
  const poner = (k: keyof typeof v) => (x: string) => setV((p) => ({ ...p, [k]: x }));
  //  El tipo actual se ofrece aunque esté desactivado: editar otros datos no obliga a cambiarlo.
  const opcionesTipo = [...(tipos.datos?.filas ?? [])];
  if (!opcionesTipo.some((t) => t.id === activo.tipo.id)) opcionesTipo.push({ ...activo.tipo, activo: false, enUso: 1 });

  async function enviar(ev: FormEvent): Promise<void> {
    ev.preventDefault();
    setEnviando(true);
    setError(null);
    const cambios: Record<string, unknown> = { versionEsperada: activo.actualizadoEn };
    if (empresa && idCentro !== (activo.centro?.id ?? '')) cambios['idCentro'] = idCentro === '' ? null : idCentro;
    if (v.nombre !== activo.nombre) cambios['nombre'] = v.nombre;
    if (v.idTipoActivo !== activo.tipo.id) cambios['idTipoActivo'] = v.idTipoActivo;
    if (v.idUbicacion !== (activo.ubicacion?.id ?? '')) cambios['idUbicacion'] = v.idUbicacion === '' ? null : v.idUbicacion;
    for (const k of ['descripcion', 'fabricante', 'modelo', 'numeroSerie', 'referenciaCliente', 'fechaInstalacion'] as const) {
      if (v[k] !== (activo[k] ?? '')) cambios[k] = v[k] === '' ? null : v[k];
    }
    try {
      await api.patch(`/api/activos/${activo.id}`, cambios);
      alGuardar();
    } catch (e) {
      setError(e);
    } finally {
      setEnviando(false);
    }
  }

  return (
    <Modal titulo={`Editar ${activo.codigo}`} cerrar={cerrar}>
      <form onSubmit={(ev) => void enviar(ev)} aria-label="Editar activo">
        {empresa ? (
          <div className="campo">
            <label htmlFor="ed-centro">Centro</label>
            <select id="ed-centro" value={idCentro} onChange={(ev) => { setIdCentro(ev.target.value); poner('idUbicacion')(''); }}>
              <option value="">Sin centro asignado</option>
              {activo.centro !== null && !(opciones.datos?.centros ?? []).some((c) => c.id === activo.centro!.id) && (
                <option value={activo.centro.id}>{activo.centro.nombre}</option>
              )}
              {(opciones.datos?.centros ?? []).map((c) => <option key={c.id} value={c.id}>{c.cliente.nombre} — {c.nombre}</option>)}
            </select>
            {campos['idCentro'] !== undefined && <div className="error-campo">{campos['idCentro']}</div>}
          </div>
        ) : (
          <p className="suave pequena">El centro ({activo.centro?.nombre}) y el código no se cambian aquí.</p>
        )}
        <CampoLibre id="ed-nombre" label="Nombre" valor={v.nombre} cambiar={poner('nombre')} error={campos['nombre']} requerido />
        <div className="campo">
          <label htmlFor="ed-tipo">Tipo</label>
          <select id="ed-tipo" value={v.idTipoActivo} onChange={(ev) => poner('idTipoActivo')(ev.target.value)}>
            {opcionesTipo.map((t) => <option key={t.id} value={t.id}>{t.nombre}{t.activo ? '' : ' (desactivado)'}</option>)}
          </select>
          {campos['idTipoActivo'] !== undefined && <div className="error-campo">{campos['idTipoActivo']}</div>}
        </div>
        <div className="campo">
          <label htmlFor="ed-ubicacion">Ubicación</label>
          <select id="ed-ubicacion" value={v.idUbicacion} disabled={idCentro === ''} onChange={(ev) => poner('idUbicacion')(ev.target.value)}>
            <option value="">Sin ubicación</option>
            {activo.ubicacion !== null && idCentro === (activo.centro?.id ?? '') && !ubicaciones.some((u) => u.id === activo.ubicacion!.id) && (
              <option value={activo.ubicacion.id}>{activo.ubicacion.nombre}</option>
            )}
            {ubicaciones.map((u) => <option key={u.id} value={u.id}>{u.ruta}</option>)}
          </select>
          {campos['idUbicacion'] !== undefined && <div className="error-campo">{campos['idUbicacion']}</div>}
        </div>
        <CampoLibre id="ed-fabricante" label="Fabricante" valor={v.fabricante} cambiar={poner('fabricante')} error={campos['fabricante']} />
        <CampoLibre id="ed-modelo" label="Modelo" valor={v.modelo} cambiar={poner('modelo')} error={campos['modelo']} />
        <CampoLibre id="ed-serie" label="Nº de serie" valor={v.numeroSerie} cambiar={poner('numeroSerie')} error={campos['numeroSerie']} />
        <CampoLibre id="ed-ref" label="Referencia del cliente" valor={v.referenciaCliente} cambiar={poner('referenciaCliente')} error={campos['referenciaCliente']} />
        <CampoLibre id="ed-fecha" label="Fecha de instalación" tipo="date" valor={v.fechaInstalacion} cambiar={poner('fechaInstalacion')} error={campos['fechaInstalacion']} />
        <CampoLibre id="ed-desc" label="Descripción" valor={v.descripcion} cambiar={poner('descripcion')} error={campos['descripcion']} multilinea />
        {/* F12.2: si no se puede mover (trabajo abierto), se enlaza qué lo impide. */}
        {error !== null && <ErrorConEnlaces error={error} />}
        <div className="fila">
          <button type="submit" className="principal" disabled={enviando}>{enviando ? 'Guardando…' : 'Guardar'}</button>
          <button type="button" onClick={cerrar}>Cancelar</button>
        </div>
      </form>
    </Modal>
  );
}

function DialogoMotivo({
  titulo, explicacion, obligatorio, textoBoton, enviar, cerrar, alTerminar, peligro = false,
}: {
  titulo: string; explicacion: string; obligatorio: boolean; textoBoton: string;
  /** UX-1B: acción destructiva (baja): el botón final, y sólo él, en rojo sólido. */
  peligro?: boolean;
  enviar: (motivo: string) => Promise<unknown>; cerrar: () => void; alTerminar: () => void;
}): ReactNode {
  const [motivo, setMotivo] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const campos = erroresDe(error);

  async function confirmar(ev: FormEvent): Promise<void> {
    ev.preventDefault();
    setEnviando(true);
    setError(null);
    try {
      await enviar(motivo);
      alTerminar();
    } catch (e) {
      setError(e);
    } finally {
      setEnviando(false);
    }
  }

  return (
    <Modal titulo={titulo} cerrar={cerrar}>
      <form onSubmit={(ev) => void confirmar(ev)} aria-label={titulo}>
        <p>{explicacion}</p>
        <div className="campo">
          <label htmlFor="motivo-activo">{obligatorio ? 'Motivo' : 'Nota (opcional)'}</label>
          <textarea id="motivo-activo" rows={3} value={motivo} required={obligatorio} minLength={obligatorio ? 5 : undefined} onChange={(ev) => setMotivo(ev.target.value)} />
          {campos['motivo'] !== undefined && <div className="error-campo">{campos['motivo']}</div>}
        </div>
        {error !== null && <ErrorCaja error={error} />}
        <div className="fila">
          <button type="submit" className={peligro ? 'peligro' : 'principal'} disabled={enviando || (obligatorio && motivo.trim().length < 5)}>{textoBoton}</button>
          <button type="button" onClick={cerrar}>Cancelar</button>
        </div>
      </form>
    </Modal>
  );
}

// ---------------------------------------------------------------- etiqueta imprimible

export function PaginaEtiquetaActivo(): ReactNode {
  const { id = '' } = useParams();
  const activo = useDatos(() => api.get<ActivoDetalle>(`/api/activos/${encodeURIComponent(id)}`), [id]);
  if (activo.cargando && activo.datos === null) return <Cargando />;
  if (activo.error !== null || activo.datos === null) {
    return (
      <div className="etiqueta-activo-pagina">
        <Vacio titulo="Este activo no existe o no tienes acceso"><Link to="/activos">Volver a Activos</Link></Vacio>
      </div>
    );
  }
  const a = activo.datos;
  return (
    <div className="etiqueta-activo-pagina">
      <div className="fila no-imprimir" style={{ marginBottom: 16 }}>
        <button className="principal" onClick={() => window.print()}>Imprimir</button>
        <Link to={`/activos/${a.id}`}>Volver a la ficha</Link>
      </div>
      <div className="etiqueta-activo" aria-label={`Etiqueta del activo ${a.codigo}`}>
        <CodigoQr texto={urlFicha(a.id)} tamano={170} titulo={`QR del activo ${a.codigo}`} />
        <div className="etiqueta-activo-texto">
          <div className="mono etiqueta-activo-codigo">{a.codigo}</div>
          <div className="etiqueta-activo-nombre">{a.nombre}</div>
          {a.titularidad === 'EMPRESA' && <div>Empresa prestadora</div>}
          <div>{a.centro?.nombre ?? 'Sin centro asignado'}</div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- catálogo de tipos

export function PaginaTiposActivo(): ReactNode {
  const tipos = useDatos(() => api.get<{ filas: TipoActivo[] }>('/api/tipos-activo?incluirInactivos=true'), []);
  const [codigo, setCodigo] = useState('');
  const [nombre, setNombre] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [editando, setEditando] = useState<{ id: string; nombre: string } | null>(null);
  const campos = erroresDe(error);

  async function crear(ev: FormEvent): Promise<void> {
    ev.preventDefault();
    setError(null);
    try {
      await api.post('/api/tipos-activo', { codigo, nombre });
      setCodigo('');
      setNombre('');
      tipos.recargar();
    } catch (e) {
      setError(e);
    }
  }

  async function cambiar(id: string, cambios: Record<string, unknown>): Promise<void> {
    setError(null);
    try {
      await api.patch(`/api/tipos-activo/${id}`, cambios);
      setEditando(null);
      tipos.recargar();
    } catch (e) {
      setError(e);
    }
  }

  return (
    <Shell migas={[{ texto: 'Mantenimiento' }, { texto: 'Tipos de activo' }]} titulo="Tipos de activo">
      <div className="panel">
        <div className="panel-cabecera"><h2>Nuevo tipo</h2></div>
        <form className="panel-cuerpo fila" style={{ flexWrap: 'wrap', alignItems: 'flex-end' }} onSubmit={(ev) => void crear(ev)} aria-label="Nuevo tipo de activo">
          <CampoLibre id="tipo-codigo" label="Código" valor={codigo} cambiar={(x) => setCodigo(x.toUpperCase())} error={campos['codigo']} requerido />
          <CampoLibre id="tipo-nombre" label="Nombre" valor={nombre} cambiar={setNombre} error={campos['nombre']} requerido />
          <button type="submit" className="principal">Añadir</button>
        </form>
        {error !== null && <div className="panel-cuerpo"><ErrorCaja error={error} /></div>}
      </div>
      <div className="panel">
        <Panel estado={tipos}>
          {(d) => (
            <div className="tabla-desliza">
              <table>
                <thead><tr><th>Código</th><th>Nombre</th><th>En uso</th><th>Estado</th><th /></tr></thead>
                <tbody>
                  {d.filas.map((t) => (
                    <tr key={t.id}>
                      <td className="mono">{t.codigo}</td>
                      <td>
                        {editando?.id === t.id ? (
                          <span className="fila">
                            <input aria-label={`Nuevo nombre de ${t.codigo}`} value={editando.nombre} onChange={(ev) => setEditando({ id: t.id, nombre: ev.target.value })} />
                            <button className="pequeno" onClick={() => void cambiar(t.id, { nombre: editando.nombre })}>Guardar</button>
                            <button className="pequeno" onClick={() => setEditando(null)}>Cancelar</button>
                          </span>
                        ) : t.nombre}
                      </td>
                      <td>{t.enUso}</td>
                      <td><span className={`etiqueta ${t.activo ? 'exito' : 'neutra'}`}>{t.activo ? 'Activo' : 'Desactivado'}</span></td>
                      <td>
                        <span className="fila">
                          {editando?.id !== t.id && <button className="pequeno" onClick={() => setEditando({ id: t.id, nombre: t.nombre })}>Renombrar</button>}
                          <button className="pequeno" onClick={() => void cambiar(t.id, { activo: !t.activo })}>{t.activo ? 'Desactivar' : 'Reactivar'}</button>
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="panel-cuerpo suave pequena">
                Un tipo nunca se borra: desactivarlo lo retira de las altas nuevas, y los activos que ya lo usan lo conservan.
              </p>
            </div>
          )}
        </Panel>
      </div>
    </Shell>
  );
}
